// @safetyhub/report-composer — 바인딩의 "양식(보고서 body) 내 위치" 파생
//
// deriveComposerWidgets가 만든 작성 페이지 위젯(파생 input)의 bindingKey는 body(ComposerNode[])의
// 어느 노드/표 셀에서 온 것이다. 작성 탭 미배치 풀에는 같은 카탈로그 라벨(예 "수준(숫자)")이 여러 번
// 반복돼 서로 구분되지 않으므로(스크린샷 제보), 각 bindingKey가 **양식 어디에 놓였는지**를 조상
// 브레드크럼(섹션 제목 › 표 라벨 › 열 헤더 › 행 헤더)으로 산출해 풀 힌트/속성창 표시에 쓴다.
//
// 조상 라벨 소스(스키마에 전용 label 필드가 없어 실 데이터 구조에서 파생):
//   - 섹션 제목: 정적 text 노드(바인딩 없는 non-empty text). 형제로 앞서거나 그룹 첫 자식으로 등장 →
//     그 이후/하위 노드의 "현재 섹션"이 된다(문서 흐름 관례. □/○/【】 머리기호 헤딩이 이 형태).
//   - 표 라벨: 표 앞 형제 헤딩(위 섹션 규칙으로 이미 ancestors에 포함) + 표 안 상단 스패닝 제목 셀.
//   - 열 헤더: 같은 열(스팬 포함) 위쪽의 정적 text 셀들(바깥 super-header → 안쪽 순).
//   - 행 헤더: 같은 행(스팬 포함) 왼쪽의 정적 text 셀들(주로 0열 라벨).
//
// 순회 구조는 walkBindings/writeForm.collectTables와 동형(group/stack.children · repeat.template ·
// branch 슬롯 · 표 셀 content). aggregate/compute 셀은 바인딩이 없어 배치 대상이 아니다.

import type { ComposerNode, TableCell, TableNode } from '@safetyhub/shared';

/** 한 바인딩의 한 배치(양식 내 한 위치). */
export interface BindingPlacement {
  /**
   * 이동/스크롤·하이라이트 대상 body 노드 id — 표 셀 바인딩이면 그 표 노드 id, 그 외 소유 노드 id.
   * (미리보기 창의 `data-composer-node-id` 앵커 스크롤·하이라이트가 이 값을 그대로 재사용.)
   */
  nodeId: string;
  /** 표 셀 바인딩일 때 셀 좌표(하이라이트 앵커 보조 식별). 노드 바인딩이면 미포함. */
  cell?: { row: number; col: number };
  /** 조상 브레드크럼(바깥→안 라벨). 라벨 없는 조상은 스킵, 연속 중복은 병합. */
  breadcrumb: string[];
}

/** 표시 라벨 정규화 — 개행/연속 공백을 단일 공백으로, 양끝 트림. 빈 문자열이면 undefined. */
function normalizeLabel(raw: string | undefined): string | undefined {
  if (raw == null) return undefined;
  const s = raw.replace(/\s+/g, ' ').trim();
  return s.length > 0 ? s : undefined;
}

/** 셀의 정적 표시 텍스트(직접 text 또는 content의 바인딩 없는 text 노드). 값 셀은 undefined. */
function staticCellText(cell: TableCell): string | undefined {
  const direct = normalizeLabel(cell.text);
  if (direct) return direct;
  for (const node of cell.content ?? []) {
    if (node.kind === 'text' && !node.binding) {
      const t = normalizeLabel(node.text);
      if (t) return t;
    }
  }
  return undefined;
}

/** 헤더 후보 셀인가 — 값(binding/compute/aggregate)이 없고 정적 텍스트가 있는 셀. */
function isStaticTextCell(cell: TableCell): boolean {
  if (cell.binding || cell.compute || cell.aggregate) return false;
  return staticCellText(cell) !== undefined;
}

/** 셀이 차지하는 좌표 범위(스팬 반영). */
function cellSpan(cell: TableCell): { r0: number; r1: number; c0: number; c1: number } {
  const r0 = cell.row;
  const c0 = cell.col;
  return {
    r0,
    c0,
    r1: r0 + (cell.rowspan ?? 1) - 1,
    c1: c0 + (cell.colspan ?? 1) - 1,
  };
}

/** (row,col) 값 셀의 열 헤더 체인 — 같은 열(스팬 겹침) 위쪽 정적 텍스트 셀을 바깥(위)→안(아래) 순으로. */
function columnHeaderCrumbs(table: TableNode, target: TableCell): string[] {
  const t = cellSpan(target);
  const found: { row: number; label: string }[] = [];
  for (const cell of table.cells) {
    if (cell === target || !isStaticTextCell(cell)) continue;
    const h = cellSpan(cell);
    if (h.r1 >= t.r0) continue; // 위쪽(엄격히 위)만
    if (h.c0 <= t.c1 && h.c1 >= t.c0) {
      const label = staticCellText(cell);
      if (label) found.push({ row: h.r0, label });
    }
  }
  found.sort((a, b) => a.row - b.row);
  return found.map((f) => f.label);
}

/** (row,col) 값 셀의 행 헤더 체인 — 같은 행(스팬 겹침) 왼쪽 정적 텍스트 셀을 바깥(좌)→안(우) 순으로. */
function rowHeaderCrumbs(table: TableNode, target: TableCell): string[] {
  const t = cellSpan(target);
  const found: { col: number; label: string }[] = [];
  for (const cell of table.cells) {
    if (cell === target || !isStaticTextCell(cell)) continue;
    const h = cellSpan(cell);
    if (h.c1 >= t.c0) continue; // 왼쪽(엄격히 왼)만
    if (h.r0 <= t.r1 && h.r1 >= t.r0) {
      const label = staticCellText(cell);
      if (label) found.push({ col: h.c0, label });
    }
  }
  found.sort((a, b) => a.col - b.col);
  return found.map((f) => f.label);
}

/** 연속 중복 라벨을 병합하며 crumb 배열을 구성한다(빈 라벨은 스킵). */
function appendCrumbs(base: string[], extra: string[]): string[] {
  const out = [...base];
  for (const label of extra) {
    if (!label) continue;
    if (out.length > 0 && out[out.length - 1] === label) continue;
    out.push(label);
  }
  return out;
}

function walk(
  nodes: ComposerNode[],
  ancestors: string[],
  out: Map<string, BindingPlacement[]>,
): void {
  const record = (bindingKey: string, placement: BindingPlacement): void => {
    const list = out.get(bindingKey) ?? [];
    list.push(placement);
    out.set(bindingKey, list);
  };

  // 이 배열 내 "현재 섹션" = 가장 최근에 등장한 정적 text 형제(바인딩 없는 헤딩). 다음 헤딩이 갱신.
  let section: string | undefined;

  for (const node of nodes) {
    // 정적 text 형제(값 아님) = 이후 형제/하위의 섹션 라벨. 자신은 기록 대상 아님.
    if (node.kind === 'text' && !node.binding) {
      const label = normalizeLabel(node.text);
      if (label) section = label;
      continue;
    }

    const nodeAncestors = section ? appendCrumbs(ancestors, [section]) : ancestors;

    if (node.binding) {
      record(node.binding.bindingKey, { nodeId: node.id, breadcrumb: nodeAncestors });
    }

    switch (node.kind) {
      case 'group':
      case 'stack':
        walk(node.children, nodeAncestors, out);
        break;
      case 'repeat':
        // 반복 노드의 행 소스 키(over)도 배치 — 행 목록 위젯(table 등, bindingKey=소스 키)이
        // 이 반복영역에서 소비되므로 그 위젯의 "양식 내 위치"다(2026-07-16 제보: 교육 종류 및
        // 실시근로자수에서 '양식 노드로 이동' 비활성 — over 연결이 배치로 안 잡힘).
        record(node.over, { nodeId: node.id, breadcrumb: nodeAncestors });
        walk([node.template], nodeAncestors, out);
        break;
      case 'branch':
        for (const slot of node.branches) {
          const slotLabel = normalizeLabel(slot.label);
          walk(slot.children, slotLabel ? appendCrumbs(nodeAncestors, [slotLabel]) : nodeAncestors, out);
        }
        break;
      case 'table':
        // 표 반복영역의 행 소스 키(over)도 배치 — repeat 노드와 동일 사유(행 목록 위젯의 소비처).
        for (const region of node.repeatRegions ?? []) {
          record(region.over, { nodeId: node.id, breadcrumb: nodeAncestors });
        }
        for (const cell of node.cells) {
          const headerCrumbs = appendCrumbs(
            columnHeaderCrumbs(node, cell),
            rowHeaderCrumbs(node, cell),
          );
          const cellAncestors = appendCrumbs(nodeAncestors, headerCrumbs);
          if (cell.binding) {
            record(cell.binding.bindingKey, {
              nodeId: node.id,
              cell: { row: cell.row, col: cell.col },
              breadcrumb: cellAncestors,
            });
          }
          if (cell.content) walk(cell.content, cellAncestors, out);
        }
        break;
      default:
        break;
    }
  }
}

/**
 * body(ComposerNode[])의 모든 바인딩에 대해 "양식 내 위치" 배치 목록을 산출한다.
 * bindingKey → 배치 배열(한 키가 여러 곳에 놓일 수 있음, 트리 순서). 템플릿당 1회만 순회하면 되므로
 * 소비처(작성 탭 풀/속성창)는 useMemo로 이 맵을 한 번 만들어 lookup만 한다.
 */
export function collectBindingPlacements(body: ComposerNode[]): Map<string, BindingPlacement[]> {
  const out = new Map<string, BindingPlacement[]>();
  walk(body, [], out);
  return out;
}
